import { createSlice } from '@reduxjs/toolkit';

/**
 * The selected location is redux state, so it dies on reload and the user lands
 * back on whichever location LocationProvider auto-picks. Persisting it is what
 * makes the global switcher feel like context rather than a filter.
 *
 * Keyed PER COMPANY: one person can hold roles at several companies, and
 * carrying company 7's location into company 8 would silently scope every
 * dashboard to a location that company cannot see. Every read is total — a
 * browser with storage disabled must fall through to today's auto-selection,
 * not throw inside a provider that renders above the whole app.
 */
export const storageKeyFor = (companyId) =>
  companyId == null ? null : `tc.selectedLocationId.${companyId}`;

export const readStoredLocationId = (companyId) => {
  const key = storageKeyFor(companyId);
  if (!key) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

export const writeStoredLocationId = (companyId, locationId) => {
  const key = storageKeyFor(companyId);
  if (!key) return;
  try {
    window.localStorage.setItem(key, locationId);
  } catch {
    /* storage disabled — the selection is still live in redux for this session */
  }
};

const initialState = {
  selectedLocationId: '',
  locations: [],
  company: null,
  user: null,
  loading: {
    locations: false,
    company: false,
  },
  error: {
    locations: null,
    company: null,
  },
};

const locationsSlice = createSlice({
  name: 'locations',
  initialState,
  reducers: {
    // Location selection
    setSelectedLocationId: (state, action) => {
      state.selectedLocationId = action.payload;
    },

    // User actions
    setUser: (state, action) => {
      state.user = action.payload;
    },

    // Company actions
    setCompanyLoading: (state, action) => {
      state.loading.company = action.payload;
    },
    setCompanySuccess: (state, action) => {
      state.company = action.payload;
      state.loading.company = false;
      state.error.company = null;
    },
    setCompanyError: (state, action) => {
      state.loading.company = false;
      state.error.company = action.payload;
    },

    // Locations actions
    setLocationsLoading: (state, action) => {
      state.loading.locations = action.payload;
    },
    setLocationsSuccess: (state, action) => {
      state.locations = action.payload;
      state.loading.locations = false;
      state.error.locations = null;
      // Note: Auto-selection logic is handled in LocationProvider's useEffect
      // to properly filter user locations by the current company
    },
    setLocationsError: (state, action) => {
      state.loading.locations = false;
      state.error.locations = action.payload;
    },

    // Reset actions
    resetLocations: (state) => {
      return initialState;
    },
    resetLocationSelection: (state) => {
      state.selectedLocationId = '';
    },
  },
});

export const {
  setSelectedLocationId,
  setUser,
  setCompanyLoading,
  setCompanySuccess,
  setCompanyError,
  setLocationsLoading,
  setLocationsSuccess,
  setLocationsError,
  resetLocations,
  resetLocationSelection,
} = locationsSlice.actions;

// Selectors
export const selectSelectedLocationId = (state) =>
  state.locations.selectedLocationId;
export const selectLocations = (state) => state.locations.locations;
export const selectCompany = (state) => state.locations.company;
export const selectUser = (state) => state.locations.user;
export const selectLocationLoading = (state) =>
  state.locations.loading.locations;
export const selectCompanyLoading = (state) => state.locations.loading.company;
export const selectLocationError = (state) => state.locations.error.locations;
export const selectCompanyError = (state) => state.locations.error.company;

// Computed selectors
export const selectCurrentLocation = (state) => {
  const selectedId = state.locations.selectedLocationId;
  const locations = state.locations.locations;

  if (selectedId === 'all') {
    return null;
  }

  return locations.find((loc) => loc.id?.toString() === selectedId) || null;
};

export const selectIsAllLocationsSelected = (state) => {
  return state.locations.selectedLocationId === 'all';
};

export const selectHasLocations = (state) => {
  return state.locations.locations.length > 0;
};

export const selectHasMultipleLocations = (state) => {
  return state.locations.locations.length > 1;
};

export const selectIsLoading = (state) => {
  return state.locations.loading.company || state.locations.loading.locations;
};

export const selectActiveLocations = (state) => {
  return state.locations.locations.filter((location) => location.isActive);
};

/**
 * Pure utility function to check if a user is admin for a specific company.
 * This is the single source of truth for admin role checking.
 * @param {Object} user - User object with access_role_instances
 * @param {Object} company - Company object with id
 * @returns {boolean} - True if user has admin role for the company
 */
export const isUserAdminForCompany = (user, company) => {
  if (!user || !company) return false;

  const userRoleInstance = user.access_role_instances?.find(
    (instance) => instance.company?.id === company.id
  );

  return userRoleInstance?.name?.toLowerCase() === 'admin';
};

/**
 * Selector to check if current user can access all locations (is admin).
 * Uses isUserAdminForCompany utility internally.
 */
export const selectUserCanAccessAllLocations = (state) => {
  const user = state.locations.user;
  const company = state.locations.company;

  return isUserAdminForCompany(user, company);
};

export default locationsSlice.reducer;
